@file:OptIn(ExperimentalComposeWebApi::class)

package org.example.project.dashboard

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.service.LoginService
import org.example.project.service.rememberActiveBuildName
import org.example.project.settings.LogoSettings
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.math.abs

private val driverDashboardJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun driverDashboardScreenLabel(screen: All?): String {
    if (screen == null) return ""
    val key = "driver_screen_${screen.key}"
    val translated = LocalizationManager.get(key)
    return translated.takeUnless { it == "[$key]" } ?: screen.label
}

private fun driverDashboardText(key: String): String = LocalizationManager.get(key)

@Composable
fun DriverDashboardLayout(
    screens: List<All>,
    selectedKey: String,
    onSelect: (String) -> Unit,
    onBack: () -> Unit,
    isPaused: Boolean,
    setPaused: (Boolean) -> Unit,
    onLogout: () -> Unit,
    onHomeTap: () -> Unit,
    onHomeLongPress: () -> Unit,
    onOpenProfile: (() -> Unit)? = null
) {
    Style(DriverStyles)
    var homePage by remember { mutableStateOf(0) }
    val isHomeDashboard = selectedKey == "DASHBOARD" || selectedKey == "HOME" || selectedKey == ""
    val t: (String) -> String = { LocalizationManager.get(it) }
    var currentUserName by remember { mutableStateOf(t("mobile_unknown_user")) }
    val taskbarHeight = 39.px
    val whiteFilter = "brightness(0) invert(1)"
    val hideDriverNav = screens.firstOrNull { it.key == selectedKey }?.hideDriverNav == true
    val activeBuildName = rememberActiveBuildName()
    val liveBadges = rememberDashboardLiveBadges(screens, includeMobileYard = false)

    LaunchedEffect(Unit) {
        val user = LoginService.getUser()
        val firstName = user?.vorname.orEmpty()
            .split(Regex("\\s+"))
            .filter { it.isNotBlank() }
            .joinToString(" ") { part ->
                val lower = part.lowercase()
                lower.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
            }
        val lastName = user?.nachname.orEmpty()
            .split(Regex("\\s+"))
            .filter { it.isNotBlank() }
            .joinToString(" ") { part ->
                val lower = part.lowercase()
                lower.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
            }
        currentUserName = when {
            firstName.isBlank() && lastName.isBlank() -> t("mobile_unknown_user")
            firstName.isBlank() -> lastName
            lastName.isBlank() -> firstName.take(1)
            else -> "${firstName.take(1)}.$lastName"
        }
    }

    if (isPaused) {
        DriverPauseOverlay(onClose = { setPaused(false) })
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            width(100.vw)
            height(100.vh)
            overflow("hidden")
            position(Position.Relative)
            backgroundColor(Color(DashboardTheme.DASHBOARD_BG))
        }
    }) {
        MobileDashboardBackground()
        Div({
            style {
                flex(1)
                position(Position.Relative)
                overflow("hidden")
                property("z-index", "1")
            }
        }) {
            Div({
                style {
                    position(Position.Relative)
                    height(100.percent)
                    width(100.percent)
                    property("z-index", "1")
                    overflowY("auto")
                    boxSizing("border-box")
                }
            }) {
                if (isHomeDashboard) {
                    when (homePage) {
                        0 -> DriverHomePage(
                            currentUserName = currentUserName,
                            whiteFilter = whiteFilter,
                            screens = screens,
                            onSelect = onSelect,
                            onAvatarClick = { onOpenProfile?.invoke() ?: onLogout() },
                            activeBuildName = activeBuildName,
                            liveBadges = liveBadges
                        )
                        1 -> DriverModulePage(
                            screens = screens,
                            whiteFilter = whiteFilter,
                            onSelect = onSelect,
                            liveBadges = liveBadges
                        )
                        else -> DriverNachrichtenPage()
                    }
                } else {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            height(100.percent)
                            width(100.percent)
                            padding(10.px)
                            boxSizing("border-box")
                        }
                    }) {
                        if (!isPaused) {
                            screens.firstOrNull { it.key == selectedKey }?.let { screen ->
                                TrackedScreenHost(screen) {
                                    screen.content?.invoke(screen)
                                }
                            }
                        }
                    }
                }
            }
        }

        if (!hideDriverNav) {
            val bottomNavigation: @Composable () -> Unit = {
                Div({
                    style {
                    height(50.px)
                    minHeight(50.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(28.px)
                    backgroundColor(rgba(13, 14, 17, 0.98))
                    property("background", "linear-gradient(180deg, rgba(29,31,36,0.96) 0%, rgba(14,15,18,0.99) 100%)")
                    property("backdrop-filter", "blur(20px)")
                    property("-webkit-backdrop-filter", "blur(20px)")
                    property("z-index", "50000")
                    property("border-top", "1px solid rgba(255,255,255,0.075)")
                    property("box-shadow", "0 -12px 34px rgba(0,0,0,0.38)")
                    }
                }) {
                DriverBottomNavItem(
                    icon = "images/arrowleft.svg",
                    isActive = false,
                    whiteFilter = whiteFilter,
                    onClick = onBack
                )
                DriverBottomNavItem(
                    icon = "images/home.svg",
                    isActive = isHomeDashboard && homePage == 0,
                    whiteFilter = whiteFilter,
                    onClick = {
                        homePage = 0
                        onHomeTap()
                    }
                )
                DriverBottomNavItem(
                    icon = "images/appliste.svg",
                    isActive = false,
                    whiteFilter = whiteFilter,
                    onClick = onHomeLongPress
                )
                }
            }
            if (selectedKey == "Fokus" || selectedKey == "AufgabenViewer") {
                AutoHidingMobileDashboardNavigation(heightPx = 50, content = bottomNavigation)
            } else {
                bottomNavigation()
            }
        }

    }
}

@Composable
private fun DriverHomePage(
    currentUserName: String,
    whiteFilter: String,
    screens: List<All>,
    onSelect: (String) -> Unit,
    onAvatarClick: () -> Unit,
    activeBuildName: String,
    liveBadges: Map<String, Int>
) {
    Div({ classes(DriverStyles.homePage) }) {
        MobileDashboardHomeHeader(liveBadges.values.sum(), currentUserName, activeBuildName, onAvatarClick)
        DriverModulePage(
            screens = screens,
            whiteFilter = whiteFilter,
            onSelect = onSelect,
            liveBadges = liveBadges
        )
    }
}

@Composable
private fun DriverModulePage(
    screens: List<All>,
    whiteFilter: String,
    onSelect: (String) -> Unit,
    liveBadges: Map<String, Int>
) {
    Div({ classes(DriverStyles.modulePage) }) {
        Div({ classes(DriverStyles.moduleGrid) }) {
            screens
                .filterNot { it.hiddenFromLauncher }
                .take(28)
                .sortedBy { driverDashboardScreenLabel(it).lowercase() }
                .forEachIndexed { index, screen ->
                    Div({
                        classes(DriverStyles.iconWrapper)
                        onClick { onSelect(screen.key) }
                        style { property("background", mobileDashboardCardSurface) }
                    }) {
                        Div({
                            classes(DriverStyles.iconBox)
                            style {
                                position(Position.Relative)
                                property("background", mobileDashboardIconSurface)
                            }
                        }) {
                            Img(src = screen.icon) {
                                style {
                                    width(42.px)
                                    height(42.px)
                                    property("filter", whiteFilter)
                                    property("filter", "$whiteFilter drop-shadow(0 0 8px rgba(211,37,57,0.24))")
                                }
                            }
                        }
                        Div({ classes(DriverStyles.cardTextArea) }) {
                            Span({ classes(DriverStyles.iconLabel) }) {
                                Text(driverDashboardScreenLabel(screen))
                            }
                            DashboardCardBadge(liveBadges[screen.key] ?: 0)
                        }
                    }
            }
        }
    }
}

@Composable
private fun DriverNachrichtenPage() {
    Div({ classes(DriverStyles.feedPage) }) {
        DriverFeedBubble(driverDashboardText("driver_feed_status_title"), driverDashboardText("driver_feed_status_stable"))
        DriverFeedBubble(driverDashboardText("driver_feed_orders_title"), driverDashboardText("driver_feed_open_tours"))
        DriverFeedBubble(driverDashboardText("driver_feed_status_title"), driverDashboardText("driver_feed_slot_free"))
        DriverFeedBubble(driverDashboardText("driver_feed_orders_title"), driverDashboardText("driver_feed_next_order"))
    }
}

@Composable
private fun DriverFeedBubble(title: String, body: String) {
    Div({ classes(DriverStyles.feedBubble) }) {
        Span({ classes(DriverStyles.feedBubbleTitle) }) { Text(title) }
        Span({ classes(DriverStyles.feedBubbleBody) }) { Text(body) }
    }
}

@Composable
fun DriverRecentsSwipeLayer(
    openKeys: List<String>,
    allScreens: List<All>,
    onDismiss: (String) -> Unit,
    onSelect: (String) -> Unit,
    onClose: () -> Unit
) {
    Div({
        classes(DriverStyles.modalOverlay)
        style {
            property("backdrop-filter", "blur(20px)")
            property("-webkit-backdrop-filter", "blur(20px)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            justifyContent(JustifyContent.FlexStart)
            paddingTop(60.px)
        }
        onClick { onClose() }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(25.px)
                overflowY("auto")
                width(100.vw)
                alignItems(AlignItems.Center)
                paddingBottom(120.px)
            }
            onClick { it.stopPropagation() }
        }) {
            openKeys.forEach { key ->
                val screen = allScreens.firstOrNull { it.key == key }
                DriverVerticalSwipeCard(
                    icon = screen?.icon ?: "",
                    label = driverDashboardScreenLabel(screen),
                    onDismiss = { onDismiss(key) },
                    onSelect = { onSelect(key) }
                )
            }
        }
    }
}

@Composable
private fun DriverVerticalSwipeCard(icon: String, label: String, onDismiss: () -> Unit, onSelect: () -> Unit) {
    var startX by remember { mutableStateOf(0.0) }
    var currentX by remember { mutableStateOf(0.0) }
    var isDragging by remember { mutableStateOf(false) }

    Div({
        style {
            width(220.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            property("transition", if (isDragging) "none" else "transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.3s")
            property("transform", "translateX(${currentX}px)")
            property("opacity", "${1.0 - (abs(currentX) / 300.0)}")
            cursor("pointer")
        }
        onTouchStart { isDragging = true; startX = it.touches.item(0)?.clientX?.toDouble() ?: 0.0 }
        onTouchMove { if (isDragging) currentX = (it.touches.item(0)?.clientX?.toDouble() ?: 0.0) - startX }
        onTouchEnd { isDragging = false; if (abs(currentX) > 130) onDismiss() else if (abs(currentX) < 10) onSelect(); currentX = 0.0 }
        onMouseDown { isDragging = true; startX = it.offsetX }
        onMouseMove { if (isDragging) currentX = it.offsetX - startX }
        onMouseUp { isDragging = false; if (abs(currentX) > 130) onDismiss() else if (abs(currentX) < 10) onSelect(); currentX = 0.0 }
    }) {
        Div({
            style {
                width(100.px)
                height(100.px)
                backgroundColor(rgba(255, 255, 255, 0.08))
                borderRadius(22.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("backdrop-filter", "blur(14px)")
                property("-webkit-backdrop-filter", "blur(14px)")
                property("border", "1px solid rgba(255,255,255,0.14)")
                property("box-shadow", "0 12px 28px rgba(0,0,0,0.28)")
            }
        }) {
            Img(src = icon) {
                style {
                    width(60.px)
                    height(60.px)
                    borderRadius(12.px)
                    property("filter", "brightness(0) invert(1)")
                }
            }
        }
        Span({
            style {
                marginTop(10.px)
                color(Color.white)
                fontSize(14.px)
                fontWeight("500")
                textAlign("center")
                property("text-shadow", "0 2px 4px rgba(0,0,0,0.5)")
            }
        }) {
            Text(label)
        }
    }
}

@Composable
private fun DriverBottomNavItem(
    icon: String,
    isActive: Boolean,
    whiteFilter: String,
    onClick: () -> Unit,
    withLongPress: Boolean = false,
    onLongPress: () -> Unit = {}
) {
    var timer by remember { mutableStateOf<Int?>(null) }
    var isLongPressDetected by remember { mutableStateOf(false) }

    Div({
        classes(if (isActive) DriverStyles.bottomNavItemActive else DriverStyles.bottomNavItem)
        onTouchStart {
            if (withLongPress) {
                isLongPressDetected = false
                timer = window.setTimeout({ isLongPressDetected = true; onLongPress() }, 500)
            }
        }
        onTouchEnd {
            timer?.let { window.clearTimeout(it) }
            timer = null
            if (!isLongPressDetected) onClick()
        }
        onMouseDown {
            if (withLongPress) {
                isLongPressDetected = false
                timer = window.setTimeout({ isLongPressDetected = true; onLongPress() }, 500)
            }
        }
        onMouseUp {
            timer?.let { window.clearTimeout(it) }
            timer = null
            if (!isLongPressDetected) onClick()
        }
    }) {
        Img(src = icon) {
            style {
                width(if (icon == "images/home.svg") 28.px else 22.px)
                height(if (icon == "images/home.svg") 28.px else 22.px)
                if (isActive) {
                    property("filter", "brightness(0) saturate(100%) invert(18%) sepia(91%) saturate(3492%) hue-rotate(343deg) brightness(91%) contrast(91%) drop-shadow(0 0 12px rgba(211,37,57,0.58))")
                } else {
                    property("filter", whiteFilter)
                }
            }
        }
    }
}

@Composable
private fun DriverPauseOverlay(onClose: () -> Unit) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    var username by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    Div({ classes(DriverStyles.modalOverlay) }) {
        Div({ classes(DriverStyles.pauseCard) }) {
            H3({ style { margin(0.px); color(Color.white) } }) { Text(t("mobile_pause_mode")) }
            Input(InputType.Text, attrs = {
                classes(DriverStyles.modernInput)
                value(username)
                onInput { username = it.value }
                attr("placeholder", t("driver_login_user_id"))
            })
            Input(InputType.Password, attrs = {
                classes(DriverStyles.modernInput)
                value(password)
                onInput { password = it.value }
                attr("placeholder", t("driver_login_password"))
            })
            Button(attrs = {
                classes(DriverStyles.accentBtn)
                onClick {
                    scope.launch {
                        if (LoginService.checkUserLogin(username, password)) {
                            onClose()
                        } else {
                            error = t("mobile_login_wrong")
                        }
                    }
                }
            }) {
                Text(t("anmelden"))
            }
            if (error != null) {
                Span({ style { color(Color.red) } }) { Text(error!!) }
            }
        }
    }
}

object DriverStyles : StyleSheet() {
    val cSurface by variable<CSSColorValue>()
    val cSilver by variable<CSSColorValue>()

    init {
        universal style {
            cSurface(rgba(12, 15, 22, 0.42))
            cSilver(rgba(255, 255, 255, 0.3))
            property("font-family", "Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
        }
    }

    val homePage by style {
        height(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(0.px)
        padding(8.px, 12.px, 6.px, 12.px)
        boxSizing("border-box")
        position(Position.Relative)
    }
    val homeAccentGlow by style {
        width(100.percent)
        height(320.px)
        position(Position.Absolute)
        top(0.px)
        left(0.px)
        property("background", "radial-gradient(circle at 50% 6%, rgba(255,42,42,0.30) 0%, rgba(255,42,42,0.08) 26%, rgba(255,42,42,0) 64%)")
        property("pointer-events", "none")
        property("z-index", "0")
    }
    val homeWallpaper by style {
        position(Position.Absolute)
        top(0.px)
        left(0.px)
        right(0.px)
        bottom(0.px)
        property("background-image", "url('${LogoSettings.mobileDashboardBackgroundLogo}')")
        property("background-repeat", "no-repeat")
        property("background-position", "center center")
        property("background-size", "72% auto")
        opacity(0.08)
        property("pointer-events", "none")
        property("z-index", "0")
    }
    val homeHeroColumn by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(10.px)
        paddingTop(8.px)
        position(Position.Relative)
        property("z-index", "2")
    }
    val homeTopBar by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(12.px)
        position(Position.Relative)
        property("z-index", "2")
    }
    val userOrb by style {
        width(112.px)
        height(112.px)
        borderRadius(50.percent)
        overflow("hidden")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        position(Position.Relative)
        property("background", "radial-gradient(circle at 34% 26%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 24%, rgba(12,15,22,0.30) 65%, rgba(12,15,22,0.62) 100%)")
        property("border", "1px solid rgba(255,255,255,0.14)")
        property("backdrop-filter", "blur(20px)")
        property("-webkit-backdrop-filter", "blur(20px)")
        property("box-shadow", "0 18px 44px rgba(0,0,0,0.36)")
    }
    val userOrbGlow by style {
        position(Position.Absolute)
        top(10.px)
        left(10.px)
        right(10.px)
        bottom(10.px)
        borderRadius(50.percent)
        property("background", "radial-gradient(circle at 50% 28%, rgba(255,86,86,0.22) 0%, rgba(255,255,255,0.05) 40%, rgba(255,255,255,0) 100%)")
        property("filter", "blur(10px)")
    }
    val homeName by style {
        margin(0.px)
        color(Color.white)
        fontSize(24.px)
        fontWeight("600")
        textAlign("center")
        position(Position.Relative)
        property("z-index", "2")
    }
    val homeUserButton by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        padding(12.px, 16.px)
        borderRadius(999.px)
        backgroundColor(Color.transparent)
        color(Color.white)
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)")
        property("border", "1px solid rgba(255,255,255,0.14)")
        property("backdrop-filter", "blur(16px)")
        property("-webkit-backdrop-filter", "blur(16px)")
        cursor("pointer")
    }
    val homeUserButtonText by style {
        color(Color.white)
        fontSize(13.px)
        fontWeight("600")
        property("white-space", "nowrap")
    }
    val driverAvatarImage by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        maxWidth(100.percent)
        maxHeight(100.percent)
        property("width", "auto")
        property("height", "auto")
        property("object-fit", "contain")
        property("transform-origin", "center center")
        property("will-change", "transform")
    }
    val driverAvatarPlaceholder by style {
        width(52.px)
        height(52.px)
        opacity(0.9)
        property("filter", "brightness(0) invert(1)")
    }
    val homeCards by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        paddingBottom(22.px)
        position(Position.Relative)
        property("z-index", "2")
    }
    val homeActionCard by style {
        width(100.percent)
        padding(18.px)
        borderRadius(24.px)
        color(Color.white)
        fontSize(16.px)
        fontWeight("600")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(12.px)
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)")
        property("border", "1px solid rgba(255,255,255,0.14)")
        property("backdrop-filter", "blur(16px)")
        property("-webkit-backdrop-filter", "blur(16px)")
        boxSizing("border-box")
        cursor("pointer")
    }
    val modulePage by style {
        width(100.percent)
        height(70.percent)
        minHeight(0.px)
        property("flex", "0 0 70%")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.FlexStart)
        padding(8.px, 4.px, 18.px, 4.px)
        boxSizing("border-box")
        overflowY("auto")
    }
    val moduleGrid by style {
        property("width", "fit-content")
        maxWidth(100.percent)
        display(DisplayStyle.Grid)
        gridTemplateColumns("repeat(4, minmax(190px, 220px))")
        gap(28.px)
        alignContent(AlignContent.Start)
        justifyContent(JustifyContent.FlexStart)
        property("margin", "0 auto")
        media(mediaMaxWidth(980.px)) {
            self style {
                gridTemplateColumns("repeat(3, minmax(190px, 220px))")
            }
        }
        media(mediaMaxWidth(679.px)) {
            self style {
                width(100.percent)
                gridTemplateColumns("repeat(2, minmax(0, 1fr))")
                gap(14.px)
            }
        }
    }
    val iconWrapper by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexStart)
        property("aspect-ratio", "2 / 1")
        minHeight(82.px)
        padding(12.px)
        boxSizing("border-box")
        borderRadius(20.px)
        property("border", "1px solid rgba(255,255,255,0.085)")
        property("border-right-color", "rgba(27,31,38,0.96)")
        property("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.055), 0 10px 24px rgba(0,0,0,0.24)")
        cursor("pointer")
        property("transition", "transform 0.16s ease, opacity 0.16s ease")
        self + ":active" style {
            property("transform", "scale(0.94)")
            opacity(0.82)
        }
    }
    val iconBox by style {
        width(40.percent)
        height(100.percent)
        borderRadius(0.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        marginRight(0.px)
        property("flex-shrink", "0")
    }
    val iconLabel by style {
        fontSize(14.px)
        fontWeight("600")
        lineHeight("1.25")
        marginTop(0.px)
        color(Color("#E7E9ED"))
        textAlign("center")
        width(100.percent)
        overflow("hidden")
        property("letter-spacing", "-0.01em")
    }
    val cardTextArea by style {
        minWidth(0.px)
        height(100.percent)
        flex(1)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(7.px)
        padding(8.px, 6.px, 8.px, 4.px)
        boxSizing("border-box")
    }
    val feedPage by style {
        minHeight(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        padding(22.px, 18.px, 28.px, 18.px)
        boxSizing("border-box")
    }
    val feedBubble by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
        padding(16.px, 18.px)
        borderRadius(22.px)
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.03) 100%)")
        property("border", "1px solid rgba(255,255,255,0.14)")
        property("backdrop-filter", "blur(14px)")
        property("-webkit-backdrop-filter", "blur(14px)")
    }
    val feedBubbleTitle by style {
        color(Color("#D32539"))
        fontSize(12.px)
        fontWeight("700")
        property("letter-spacing", "0.08em")
        property("text-transform", "uppercase")
    }
    val feedBubbleBody by style {
        color(Color.white)
        fontSize(14.px)
        lineHeight("1.5")
    }
    val bottomNavItem by style {
        width(68.px)
        height(100.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        cursor("pointer")
    }
    val bottomNavItemActive by style {
        width(68.px)
        height(100.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        cursor("pointer")
        borderRadius(14.px)
        property("background", "linear-gradient(180deg, rgba(211,37,57,0.20), rgba(211,37,57,0.07))")
        property("border", "1px solid rgba(211,37,57,0.28)")
        property("filter", "drop-shadow(0 0 12px rgba(211,37,57,0.38))")
    }
    val modalOverlay by style {
        position(Position.Fixed)
        top(0.px)
        left(0.px)
        right(0.px)
        bottom(0.px)
        backgroundColor(rgba(0, 0, 0, 0.72))
        property("z-index", "99999")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
    }
    val pauseCard by style {
        backgroundColor(cSurface.value())
        property("background", "linear-gradient(180deg, rgba(14,18,26,0.92) 0%, rgba(10,12,18,0.84) 100%)")
        property("backdrop-filter", "blur(20px)")
        property("-webkit-backdrop-filter", "blur(20px)")
        padding(30.px)
        borderRadius(20.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(15.px)
        width(280.px)
        property("border", "1px solid rgba(255,255,255,0.14)")
        property("box-shadow", "0 18px 46px rgba(0,0,0,0.34)")
    }
    val accentBtn by style {
        padding(12.px)
        backgroundColor(Color.transparent)
        color(Color.white)
        property("background", "rgba(255,255,255,0.04)")
        property("border", "1px solid rgba(255,255,255,0.16)")
        borderRadius(10.px)
        property("box-shadow", "none")
    }
    val modernInput by style {
        width(100.percent)
        padding(12.px)
        borderRadius(10.px)
        backgroundColor(Color.transparent)
        color(Color.white)
        property("background", "rgba(255,255,255,0.03)")
        property("border", "1px solid ${cSilver.value()}")
        property("box-sizing", "border-box")
    }
}

private suspend fun loadDriverDashboardProfileImages(currentUser: All): List<All> {
    val api = if (currentUser.firmenId.isNotBlank()) {
        "profilverwaltung/documents/${driverDashboardApiPart(currentUser.firmenId)}/${driverDashboardApiPart(currentUser.id)}"
    } else {
        "profilverwaltung/documents/${driverDashboardApiPart(currentUser.id)}"
    }
    val raw = runCatching { LumaticaApiManager.call(api) }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { driverDashboardJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private fun driverDashboardApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
